import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAppDispatch, useAppSelector } from '../App/Hooks/reduxHooks'
import { useWindowDimensions } from '../Common/Hooks/useWindowDimensions'

import { getReferral } from './apiAction/getReferral'
import referralImage from './assets/referral-image.png'
import { GenerateMessage } from './components/Messages/GenerateMessage'
import { Table } from './components/Table/Table'
import { shareButtonConfig } from './config/shareButtonsConfig'
import { referralListSelector } from './referralSlice'
import {
  ButtonsContainer,
  CopyButton,
  CopyButtonContainer,
  CopyButtonIcon,
  CopyButtonText,
  CopyContainer,
  CopyLinkInput,
  CopyMessage,
  CopyMessageText,
  HorizontalLine,
  ReferralBanner,
  ReferralBannerImage,
  ReferralBannerText,
  ReferralBannerTextWrapper,
  ReferralMainWrapper,
  ReferralStepsWrapper,
  ShareButton,
  ShareButtonContainer,
  ShareIcon,
  ShareText,
  StepsHeading,
  StepsHighlightText,
  StepWrapper,
} from './style'

export const Referral = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { windowWidth } = useWindowDimensions()

  const [isCopyMessageVisible, setIsCopyMessageVisible] =
    useState<boolean>(false)
  const [link, setLink] = useState<string>('Loading...')

  const referral = useAppSelector(referralListSelector)

  useEffect(() => {
    dispatch(getReferral())
  }, [dispatch])

  useEffect(() => {
    if (referral) {
      setLink(referral.referralLink)
    }
  }, [referral])

  const message: string = `${t(
    'I am a proud member of the Optios family',
  )}🔥.\n${t('Follow this link and get 1 month for free')}!\n🚀`

  useEffect(() => {
    let timer: string | number | NodeJS.Timeout | undefined
    if (isCopyMessageVisible) {
      timer = setTimeout(() => {
        setIsCopyMessageVisible(false)
      }, 4000)
    }
    return () => {
      clearTimeout(timer)
    }
  }, [isCopyMessageVisible])

  const handleCopyButton = () => {
    setIsCopyMessageVisible(true)
    navigator.clipboard.writeText(`${link}`)
  }

  const config = shareButtonConfig(message, link)

  return (
    <ReferralMainWrapper windowWidth={windowWidth}>
      <ReferralBanner>
        <ReferralBannerTextWrapper>
          <ReferralBannerText windowWidth={windowWidth}>
            {t('Invite a colleague and get one month for free')}!
          </ReferralBannerText>
        </ReferralBannerTextWrapper>
        <ReferralBannerImage windowWidth={windowWidth} src={referralImage} />
      </ReferralBanner>
      <ReferralStepsWrapper>
        <StepWrapper>
          <StepsHeading>
            1. {t('Share your link with your colleagues')}!
          </StepsHeading>
          <CopyContainer windowWidth={windowWidth}>
            <CopyLinkInput windowWidth={windowWidth} disabled value={link} />
            <CopyButton windowWidth={windowWidth} onClick={handleCopyButton}>
              <CopyButtonContainer>
                <CopyButtonIcon icon={'copy'} />
                <CopyButtonText>{t('Copy link')}</CopyButtonText>
              </CopyButtonContainer>
            </CopyButton>
            {isCopyMessageVisible && (
              <CopyMessage isVisible={isCopyMessageVisible}>
                <CopyMessageText>{t('Copied')}!</CopyMessageText>
              </CopyMessage>
            )}
          </CopyContainer>
          <ButtonsContainer windowWidth={windowWidth}>
            {config.map((button, index) => (
              <ShareButton key={index} windowWidth={windowWidth}>
                <ShareButtonContainer onClick={button.onClick}>
                  <ShareIcon icon={button.icon} />
                  <ShareText>{t(button.text)}</ShareText>
                </ShareButtonContainer>
              </ShareButton>
            ))}
          </ButtonsContainer>
        </StepWrapper>
        <StepWrapper>
          <StepsHeading>
            2.{' '}
            {t(
              'If they register through the link, they will receive a free-trial for 14 days',
            )}
            .
          </StepsHeading>
        </StepWrapper>
        <StepWrapper>
          <StepsHeading>
            3.{' '}
            {t(
              'As soon as your colleague becomes a customer you will be rewarded with',
            )}{' '}
            <StepsHighlightText>
              {t('1 month of your current package for free')}
            </StepsHighlightText>
            !
          </StepsHeading>
          <HorizontalLine />

          {referral && (
            <GenerateMessage
              total={referral.total}
              redeemed={
                referral.list.filter(item => item.redeemedAt !== null).length
              }
            />
          )}

          {referral && <Table referral={referral.list} />}
        </StepWrapper>
      </ReferralStepsWrapper>
    </ReferralMainWrapper>
  )
}
